import { AlertTriangle, RefreshCw, WifiOff } from "lucide-react";
import { ApiError, isConnectivityError } from "@/lib/api";
import { S, T } from "@/lib/strings";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

/**
 * A failed *query* renders inline, in the shape of the thing it replaced, with
 * the status code visible — not a toast. A toast for a load failure disappears
 * before the operator has finished reading it and leaves an empty panel behind
 * that looks like "no data".
 *
 * Failed *mutations* are the opposite: those are toasts, because the page
 * behind them is still valid.
 */
export function QueryErrorCard({
  error,
  onRetry,
  className,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  title?: string;
}) {
  const api = error instanceof ApiError ? error : null;
  const connectivity = isConnectivityError(error);
  const Icon = connectivity ? WifiOff : AlertTriangle;

  return (
    <div
      role="alert"
      className={cn(
        "rounded-lg border border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.04)] p-4",
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <Icon size={15} className="mt-0.5 shrink-0 text-[#fca5a5]" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[var(--color-fg)]">
            {title ?? (connectivity ? S.connOffline : S.queryFailedTitle)}
          </div>
          <p className="mt-1 break-words text-xs text-[var(--color-fg-subtle)]">
            {connectivity ? S.connOfflineHint : (api?.message ?? String(error))}
          </p>
          {api && (
            <p className="mt-1 font-mono text-[11px] text-[var(--color-fg-faint)]">
              {T.httpStatus(api.status, api.code)}
            </p>
          )}
        </div>
        {onRetry && (
          <Button size="sm" variant="default" onClick={onRetry}>
            <RefreshCw size={12} />
            {S.retry}
          </Button>
        )}
      </div>
    </div>
  );
}
